/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useMemo } from 'react'

import styled, { css } from 'styled-components'

import { PrimaryButton } from 'src/components/Button'
import FlexBox from 'src/components/Common/FlexBox'
import Icon from 'src/components/Icon'
import { useThemeContext } from 'src/contexts/ThemeContext'
import { shortenAddress } from 'src/functions/address/shortenAddress'
import { pxToVw } from 'src/functions/units'
import { DonationResultStatus } from 'src/types/api'
import { CouldBe } from 'src/types/main'
import { HexColor } from 'src/types/theme'

const statusColorsMap: Record<DonationResultStatus, [HexColor, string]> = {
  unpaid: ['#FFC01F', 'rgba(255, 192, 31, .15)'],
  paid: ['#7CD366', 'rgba(35, 255, 31, .15)'],
  pending: ['#7D7D7D', '#C5C8CD'],
  success: ['#7CD366', 'rgba(35, 255, 31, .15)'],
  failed: ['#D36666', 'rgba(255, 31, 31, .15)'],
  blocked: ['#D36666', 'rgba(255, 31, 31, .15)']
}

type Props = {
  description: string
  donationAmount: number
  expiredAt: number
  blockReason: string
  status: DonationResultStatus
  transactionHash: CouldBe<string>
  withRedemption: boolean
  onProceedToPaymentClick: () => Promise<void>
}

const HistorySummary = ({
  description,
  donationAmount,
  blockReason,
  expiredAt,
  status,
  transactionHash,
  withRedemption,
  onProceedToPaymentClick
}: Props) => {
  const { colors } = useThemeContext()

  const derivedStatus: DonationResultStatus = useMemo(() => {
    if (blockReason) {
      return 'blocked'
    }

    if (status === 'unpaid' && expiredAt < new Date().getTime()) {
      return 'failed'
    }

    return status
  }, [expiredAt, status, blockReason])

  return (
    <StyledContainer columnGap={18} flex={1}>
      <StyledStatusCircle $status={derivedStatus} />

      <StyledSummaryContentWrap
        columnGap={18}
        alignItems="center"
        justifyContent="space-between"
        flex={1}
      >
        <FlexBox flexDirection="column" rowGap={14}>
          <FlexBox columnGap={17} alignItems={'center'}>
            <StyledStatusPill
              $status={derivedStatus}
            >{`${derivedStatus[0].toUpperCase()}${derivedStatus.substring(1)}`}</StyledStatusPill>
            <StyledAmount>SGD ${donationAmount}</StyledAmount>
          </FlexBox>

          <StyledPaymentDescription>
            <em>Payment Method:</em> {description}
            <br />
            <em>NFT Redemption:</em> {withRedemption ? 'Yes' : 'No'}
            {transactionHash && (
              <>
                <br />
                <em>Transaction Hash:</em>{' '}
                <FlexBox as="span" inline alignItems="center" columnGap={8}>
                  <a
                    href={`https://etherscan.io/tx/${transactionHash}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {shortenAddress(transactionHash, 16, 16)}
                  </a>
                  <Icon icon="new-tab" size={16} color={colors.primary600} />
                </FlexBox>
              </>
            )}
            {blockReason ? (
              <>
                <br />
                <em>Block Reason:</em> {blockReason}
              </>
            ) : null}
          </StyledPaymentDescription>

          {derivedStatus === 'unpaid' && (
            <StyledPrimaryButton
              size="small"
              borderRadius="small"
              onClick={onProceedToPaymentClick}
            >
              Proceed to Donation Payment
            </StyledPrimaryButton>
          )}
        </FlexBox>

        {/*<StyledExpandCollapseButton></StyledExpandCollapseButton>*/}
      </StyledSummaryContentWrap>
    </StyledContainer>
  )
}

export default HistorySummary

const StyledContainer = styled(FlexBox)`
  width: 100%;
`

const StyledStatusCircle = styled.i<{
  $status: DonationResultStatus
}>`
  width: ${pxToVw(20)};
  height: ${pxToVw(20)};
  background-color: ${({ $status }) => statusColorsMap[$status][0]};
  border-radius: ${pxToVw(10)};
  margin-top: ${pxToVw(8)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: ${pxToVw(20, 720)};
      height: ${pxToVw(20, 720)};
      border-radius: ${pxToVw(10, 720)};
      margin-top: ${pxToVw(8, 720)};
    `}
  `}
`

const StyledSummaryContentWrap = styled(FlexBox)`
  padding-bottom: ${pxToVw(24)};
  border-bottom: solid 1px ${({ theme }) => theme.colors.gray200};

  > div {
    width: 100%;
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding-bottom: ${pxToVw(24, 720)};
    `}
  `}
`

const StyledStatusPill = styled.div<{
  $status: DonationResultStatus
}>`
  font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body3)};
  color: ${({ $status }) => statusColorsMap[$status][0]};
  line-height: 1em;
  background-color: ${({ $status }) => statusColorsMap[$status][1]};
  padding: ${pxToVw(9)} ${pxToVw(24)};
  border-radius: ${pxToVw(36 / 2)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body3, 720)};
      padding: ${pxToVw(9, 720)} ${pxToVw(24, 720)};
      border-radius: ${pxToVw(36 / 2, 720)};
    `}
  `}
`

const StyledAmount = styled.em`
  font-size: ${({ theme }) => pxToVw(theme.units.fontSize.title)};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray500};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${({ theme }) => pxToVw(theme.units.fontSize.title, 720)};
    `}
  `}
`

const StyledPaymentDescription = styled.div`
  font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body3)};
  color: ${({ theme }) => theme.colors.gray};

  > em {
    display: inline-block;
    color: ${({ theme }) => theme.colors.gray500};
    font-weight: 500;

    &:not(:last-child) {
      margin-bottom: ${pxToVw(7)};
    }
  }

  a,
  a:visited {
    display: inline-block;
    color: ${({ theme }) => theme.colors.primary600};

    &:hover {
      text-decoration: underline;
    }
  }

  ${({ theme }) => theme.mediaQuery.mobile`
    font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body3, 720)};

    > em:not(:last-child) {
      margin-bottom: ${pxToVw(7, 720)};
    }
  `}
`

const StyledPrimaryButton = styled(PrimaryButton)`
  width: 100%;
`
